/**
 * Platform roles, mirroring the backend's derived role strings. The frontend
 * uses these only to reflect access (nav visibility, route guards) - the
 * backend remains the source of truth and re-checks every request.
 */
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  STARTUP_ADMIN: 'startup_admin',
  STARTUP_MEMBER: 'startup_member',
  INVESTOR_ADMIN: 'investor_admin',
  INVESTOR_MEMBER: 'investor_member',
  TRUSTED_PARTNER_ADMIN: 'trusted_partner_admin',
  TRUSTED_PARTNER_MEMBER: 'trusted_partner_member',
  INDIVIDUAL: 'individual',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * True when the user holds at least one of the allowed roles. An empty
 * `allowed` list means "any authenticated user".
 */
export function hasAnyRole(userRoles: readonly string[], allowed: readonly string[]): boolean {
  if (allowed.length === 0) return true;
  return userRoles.some((r) => allowed.includes(r));
}

/**
 * Priority order used to pick a single role for display and landing decisions
 * (welcome copy, per-role dashboard/profile) when a user holds several. Highest
 * privilege wins; `individual` is the floor.
 */
const ROLE_PRIORITY: readonly Role[] = [
  ROLES.SUPER_ADMIN,
  ROLES.STARTUP_ADMIN,
  ROLES.INVESTOR_ADMIN,
  ROLES.TRUSTED_PARTNER_ADMIN,
  ROLES.STARTUP_MEMBER,
  ROLES.INVESTOR_MEMBER,
  ROLES.TRUSTED_PARTNER_MEMBER,
  ROLES.INDIVIDUAL,
];

/**
 * The single role a role-adaptive view should key off. Returns the highest
 * priority role the user holds, falling back to `individual` when the list is
 * empty or unrecognized.
 */
export function getPrimaryRole(userRoles: readonly string[]): Role {
  return ROLE_PRIORITY.find((role) => userRoles.includes(role)) ?? ROLES.INDIVIDUAL;
}
